import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus, Wand2 } from 'lucide-react'
import { toast } from 'sonner'
import { DataTable } from '@/components/data-table'
import { Notice, PageHeader } from '@/components/page'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ApiError } from '@/lib/api'
import { MessageEditor } from '@/features/i18n/message-editor'
import { LanguageStatus } from '@/features/i18n/status-badge'
import {
  PLATFORMS,
  useLanguages,
  useMessageDetail,
  useMessages,
  useModules,
  useTranslateMissing,
  type MessageRow,
  type TranslationState,
} from '@/features/i18n/api'

const PAGE_SIZE = 20

/**
 * Radix Select cannot hold an empty-string value — it uses "" internally for
 * "nothing selected" — so "no filter" needs a sentinel, converted at the two
 * boundaries below rather than leaking into the query.
 */
const ANY = '__any'

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toISOString().slice(0, 10)
}

function LegendItem({ status, label }: { status: TranslationState; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <LanguageStatus code="●" status={status} />
      {label}
    </span>
  )
}

export function MessagesPage() {
  const [page, setPage] = useState(1)
  const [keyword, setKeyword] = useState('')
  const [moduleCode, setModuleCode] = useState('')
  const [platform, setPlatform] = useState('')
  const [lang, setLang] = useState('')
  const [status, setStatus] = useState<TranslationState | ''>('')
  const [reviewOnly, setReviewOnly] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)

  const modules = useModules()
  const languages = useLanguages()
  const translateMissing = useTranslateMissing()

  const query = useMessages({
    page,
    pageSize: PAGE_SIZE,
    keyword,
    module: moduleCode,
    platform,
    lang,
    status: status || undefined,
    needsReview: reviewOnly,
  })

  // The editor seeds react-hook-form from this, so it must not render until the
  // data has arrived — `useForm` captures its defaults on first render, and
  // filling them in later paints empty inputs over whatever was typed.
  const detail = useMessageDetail(editingId)

  const targetLanguages = useMemo(
    () => (languages.data ?? []).filter((language) => language.enabled && !language.is_baseline),
    [languages.data],
  )

  const total = query.data?.total ?? 0

  const columns = useMemo<ColumnDef<MessageRow, unknown>[]>(
    () => [
      {
        id: 'key',
        header: 'Key',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-mono text-xs">{row.original.message_key}</div>
            <span className="mt-1 inline-block rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
              {row.original.module}
            </span>
          </div>
        ),
      },
      {
        id: 'english',
        header: 'English (baseline)',
        cell: ({ row }) => {
          const value = row.original.baseline_content
          // null and '' are different states and both are real in this corpus,
          // so neither is rendered as ordinary empty copy.
          if (value === null) {
            return <span className="text-xs italic text-[#a9760c]">no English row</span>
          }
          if (value === '') {
            return <span className="text-xs italic text-muted-foreground">blank</span>
          }
          return <span className="line-clamp-2 max-w-[280px] text-[13px]">{value}</span>
        },
      },
      {
        id: 'status',
        header: 'Translations',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.translations.map((entry) => (
              <LanguageStatus
                key={entry.language_code}
                code={entry.language_code}
                status={entry.status}
              />
            ))}
          </div>
        ),
      },
      {
        id: 'updated',
        header: 'Updated',
        cell: ({ row }) => (
          <div className="whitespace-nowrap text-xs text-muted-foreground">
            <div>{formatDate(row.original.updated_at)}</div>
            {row.original.updated_by && (
              <div className="text-[11px] opacity-70">{row.original.updated_by}</div>
            )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button variant="secondary" size="sm" onClick={() => setEditingId(row.original.id)}>
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <>
      <PageHeader
        title="Messages"
        actions={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={translateMissing.isPending}
              onClick={async () => {
                try {
                  // `mutateAsync` takes its variables positionally, so an optional
                  // scope still has to be passed. Omitting it is a type error the
                  // bare `tsc --noEmit` misses — the root tsconfig has `files: []`,
                  // so only `tsc -b` (what `pnpm build` runs) compiles this file.
                  const result = await translateMissing.mutateAsync(undefined)
                  toast.success(
                    result.queued === 0
                      ? 'Nothing missing — every key has a translation in every language.'
                      : `Queued ${result.queued} key${result.queued === 1 ? '' : 's'}. ${result.skipped_manual} human translation${result.skipped_manual === 1 ? '' : 's'} left untouched.`,
                  )
                } catch (error) {
                  toast.error(error instanceof ApiError ? error.message : 'Could not start the backfill.')
                }
              }}
            >
              <Wand2 />
              Fill missing
            </Button>
            <Button onClick={() => setCreating(true)}>
              <Plus />
              New message
            </Button>
          </div>
        }
      />

      <Notice>
        <b>English is the single source of truth.</b> Editing it re-translates machine drafts
        automatically and flags human translations for review — it never overwrites a human edit. A
        key with no translation still ships, carrying the English string, so{' '}
        <b>untranslated copy looks intentional to readers</b> — the badges below are the only place
        that shows otherwise.
      </Notice>

      <Card className="mb-3 space-y-2 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={moduleCode || ANY}
            onValueChange={(value) => {
              setModuleCode(value === ANY ? '' : value)
              setPage(1)
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All modules" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All modules</SelectItem>
              {(modules.data ?? []).map((module) => (
                <SelectItem key={module.code} value={module.code}>
                  {module.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={platform || ANY}
            onValueChange={(value) => {
              setPlatform(value === ANY ? '' : value)
              setPage(1)
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="All platforms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All platforms</SelectItem>
              {PLATFORMS.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={lang || ANY}
            onValueChange={(value) => {
              const next = value === ANY ? '' : value
              setLang(next)
              // The status filter is meaningless across every language at once —
              // "show me the machine translations" would match nearly every row —
              // so the backend ignores it without a language. Clearing it here
              // keeps the controls honest rather than leaving a set filter that
              // quietly does nothing.
              if (!next && status !== 'failed') setStatus('')
              setPage(1)
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Any language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any language</SelectItem>
              {targetLanguages.map((language) => (
                <SelectItem key={language.code} value={language.code}>
                  {language.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={status || ANY}
            onValueChange={(value) => {
              setStatus(value === ANY ? '' : (value as TranslationState))
              setPage(1)
            }}
          >
            <SelectTrigger
              className="w-40"
              title={lang ? undefined : 'Pick a language to filter by any status except Translation failed'}
            >
              <SelectValue placeholder="Any status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any status</SelectItem>
              <SelectItem value="human" disabled={!lang}>Human-reviewed</SelectItem>
              <SelectItem value="machine" disabled={!lang}>Machine draft</SelectItem>
              <SelectItem value="needs_review" disabled={!lang}>Needs review</SelectItem>
              <SelectItem value="missing" disabled={!lang}>Not translated</SelectItem>
              {/* The one status that also works across every language: it is
                  rare enough that "failed anywhere" is a useful list. */}
              <SelectItem value="failed">Translation failed</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant={reviewOnly ? 'default' : 'secondary'}
            size="sm"
            onClick={() => {
              setReviewOnly((current) => !current)
              setPage(1)
            }}
          >
            Needs review
          </Button>

          {(moduleCode || platform || lang || status || reviewOnly || keyword) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setModuleCode('')
                setPlatform('')
                setLang('')
                setStatus('')
                setReviewOnly(false)
                setKeyword('')
                setPage(1)
              }}
            >
              Clear
            </Button>
          )}

          <span className="ml-auto text-xs text-muted-foreground">
            <b className="text-foreground">{total}</b> key{total === 1 ? '' : 's'} ·{' '}
            {targetLanguages.length} target language{targetLanguages.length === 1 ? '' : 's'}
          </span>
        </div>
      </Card>

      <Card>
        <DataTable
          columns={columns}
          query={query}
          // Server-side, and it has to be: the list is paginated, so filtering
          // the fetched page in the browser would report "no matches" for every
          // key past the boundary — identical to the key not existing.
          search={{
            value: keyword,
            onChange: (value) => {
              setKeyword(value)
              setPage(1)
            },
            placeholder: 'Search key or English text…',
          }}
          pagination={{ page, onPageChange: setPage }}
          emptyMessage="No messages in the catalog yet."
          isFiltered={Boolean(moduleCode || platform || lang || status || reviewOnly)}
        />

        {/*
          The legend is not decoration. Every badge in the table is a coloured
          language code, and the grey one is the state that fails silently —
          an untranslated key still ships, carrying English, so a Japanese
          screen renders readable English and looks finished. This is the only
          place on the screen that says otherwise.
        */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-border px-3 py-2.5 text-xs text-muted-foreground">
          <LegendItem status="human" label="Human-reviewed — protected from automated overwrite" />
          <LegendItem status="machine" label="Machine draft" />
          <LegendItem status="needs_review" label="English changed underneath" />
          <LegendItem status="missing" label="Not translated — ships the English text" />
          <LegendItem status="failed" label="Translation failed — ships the English text" />
        </div>
      </Card>

      {creating && (
        <MessageEditor open onOpenChange={(open) => !open && setCreating(false)} />
      )}

      {editingId !== null && detail.data && (
        <MessageEditor
          key={detail.data.id}
          detail={detail.data}
          // The editor collects a regenerated draft by re-reading the key —
          // retranslation runs in the background, so there is nothing in the
          // response to show.
          refetchDetail={async () => (await detail.refetch()).data}
          open
          onOpenChange={(open) => !open && setEditingId(null)}
        />
      )}
    </>
  )
}
